import { supabase } from './supabase'
// ── Ambient company: client side ───────────────────────────────────────────────
// One RPC (show_company) answers "who else is on these shows" for up to 60 ids at
// a time; results are cached for ten minutes per show so the deck, Tonight and a
// show page don't each ask again. Calls coalesce: everything requested in the same
// tick goes out as one batch. Supabase is imported lazily so this file loads under
// Node's test runner (ship-gate rule).
const TTL_MS = 10 * 60 * 1000
const cache = new Map()      // showId → { at, row }
const inflight = new Map()   // showId → Promise<row|null>
let pending = new Set()
let timer = null

export function cachedCompany(showId) {
  const c = cache.get(showId)
  return c && Date.now() - c.at < TTL_MS ? c.row : undefined
}

export function invalidateCompany(showId) {
  if (showId == null) cache.clear(); else cache.delete(showId)
}

async function flush() {
  const ids = [...pending].filter(n => Number.isInteger(n) && n > 0).slice(0, 60)
  const asked = pending; pending = new Set(); timer = null
  let rows = []
  try {
    const { data, error } = await supabase.rpc('show_company', { p_show_ids: ids })
    if (!error && Array.isArray(data)) rows = data
  } catch { /* offline or signed out: resolve nulls, try again next time */ }
  const byId = new Map(rows.map(r => [r.show_id, r]))
  for (const id of asked) {
    const row = byId.get(id) || null
    if (rows.length || row) cache.set(id, { at: Date.now(), row })
    const res = inflight.get(id); inflight.delete(id)
    res?.(row)
  }
}

// Resolves to the show's row ({ show_id, watching, marked_week, near }) or null.
export function fetchCompany(showId) {
  const hit = cachedCompany(showId)
  if (hit !== undefined) return Promise.resolve(hit)
  if (inflight.has(`p:${showId}`)) return inflight.get(`p:${showId}`)
  const p = new Promise(resolve => { inflight.set(showId, resolve) })
  inflight.set(`p:${showId}`, p)
  p.finally(() => inflight.delete(`p:${showId}`))
  pending.add(showId)
  if (!timer) timer = setTimeout(flush, 40)
  return p
}

// The door: visible people within a season of you on this show.
export async function fetchCompanions(showId, limit = 6) {
  try {
    const { data, error } = await supabase.rpc('show_companions', { p_show: showId, p_limit: limit })
    if (error) return []
    return Array.isArray(data) ? data : []
  } catch { return [] }
}

// Near matches: visible people whose library overlaps yours (shared shows ×3 +
// shared genres), not already followed. Returns a concrete shared show name and the
// top two genre names so a row can say why.
export async function fetchMatches(limit = 5) {
  try {
    const { data, error } = await supabase.rpc('taste_matches_v2', { p_limit: limit })
    if (error) return []
    return Array.isArray(data) ? data : []
  } catch { return [] }
}

// Who's with you (2026-10-18): episode-level positions of people YOU follow on this
// show (friend_positions, definer; _company_visible still applies). Strangers stay
// season-only. Short cache so the deck card and the show page don't double-fetch.
const _friends = new Map()
export async function fetchFriendPositions(showId, limit = 5) {
  const k = `${showId}:${limit}`
  const hit = _friends.get(k)
  if (hit && Date.now() - hit.at < 60000) return hit.rows
  try {
    const { data } = await supabase.rpc('friend_positions', { p_show: showId, p_limit: limit })
    const rows = Array.isArray(data) ? data : []
    _friends.set(k, { at: Date.now(), rows })
    return rows
  } catch { return [] }
}
